import type { BillingProviderStatus, PaymentMethodSummary, RequiredSubscriptionAction, SubscriptionAccessMode } from './types';

/**
 * EPN-105 — wire types for the billing endpoints in docs/billing-v1-contract.md section 3. Money is integer CENTS,
 * JSON is camelCase, enums are strings. These describe EPN SOFTWARE-SUBSCRIPTION billing only; nothing here ever
 * relates to payroll funds, direct deposit or tax deposits.
 */

/** `GET /api/subscription/access` — what the caller's company may do right now, as the server decided. */
export interface SubscriptionAccessResult {
  state: string;
  mode: SubscriptionAccessMode;
  reason: string | null;
  requiredAction: RequiredSubscriptionAction | null;
  gracePeriod: boolean;
  graceEndsUtc: string | null;
}

export interface NextInvoiceEstimate {
  baseCents: number;
  perEmployeeCents: number;
  billableEmployees: number;
  estimatedTotalCents: number;
  /** The server's own words about tax (for example that it is calculated at checkout). Shown verbatim. */
  taxNote: string | null;
}

/** `GET /api/billing/status` */
export interface BillingStatusResponse {
  provider: BillingProviderStatus;
  paymentMethod: PaymentMethodSummary | null;
  nextInvoiceEstimate: NextInvoiceEstimate | null;
  graceEndsUtc: string | null;
  nextBillingUtc: string | null;
}

/** `POST /api/billing/checkout-sessions` and `POST /api/billing/portal-sessions` (the latter has `url` only). */
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

export type PlanChangeKind = 'Upgrade' | 'Downgrade' | 'Same';

export type InvoiceLineKind = 'Subscription' | 'Usage' | 'Proration' | 'Tax' | 'Other';

export interface InvoiceLine {
  description: string;
  amountCents: number;
  quantity: number;
  kind: InvoiceLineKind;
}

/** `GET /api/billing/plan-change-preview?planCode=` — shown to the user BEFORE they confirm. `lines` carry the same
 * shape as an invoice's lines (the contract names the field but not its shape; see the report's contract notes). */
export interface PlanChangePreview {
  kind: PlanChangeKind;
  amountDueNowCents: number;
  taxCents: number;
  currency: string;
  effectiveAtUtc: string;
  lines: InvoiceLine[];
  /** The usable features this change gains (`Gained`, an upgrade) or stops allowing new activity for (`ReadOnly`, a
   * downgrade). Optional: an older server omits it. Nothing is deleted either way. */
  featuresAffected?: PlanChangeFeatureImpact[] | null;
  /** Plain words for what a downgrade does to data: nothing is deleted, history stays viewable and exportable, new
   * activity is blocked until the plan returns. Null for an upgrade or when the server has nothing to add. */
  consequenceNote?: string | null;
}

export type PlanChangeConsequence = 'Gained' | 'ReadOnly';

export interface PlanChangeFeatureImpact {
  featureKey: string;
  displayName: string;
  category: string;
  readinessStatus: string;
  requiredPlanName: string;
  consequence: PlanChangeConsequence;
}

export interface PlanChangeRequest {
  planCode: string;
  idempotencyKey: string;
  /** The amount the user saw and agreed to. The server re-checks it and answers 409 `PREVIEW_CHANGED` if it moved. */
  expectedAmountDueNowCents: number;
}

export type InvoiceStatus = 'Draft' | 'Open' | 'Paid' | 'Void' | 'Uncollectible';

export interface InvoiceSummary {
  id: string;
  number: string | null;
  status: InvoiceStatus;
  currency: string;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  amountPaidCents: number;
  amountRefundedCents: number;
  periodStartUtc: string | null;
  periodEndUtc: string | null;
  issuedAtUtc: string;
  paidAtUtc: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
}

export interface InvoiceDetail extends InvoiceSummary {
  lines: InvoiceLine[];
}

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export type BillingHistoryKind =
  | 'Invoice' | 'Payment' | 'Refund' | 'PlanChange' | 'TrialStarted' | 'PaymentFailed' | 'Cancellation';

export interface BillingHistoryItem {
  kind: BillingHistoryKind;
  occurredAtUtc: string;
  summary: string;
  amountCents: number | null;
  reference: string | null;
}

/** The approved billing-support issue types (see `BillingSupportRequest.IssueType`). */
export const SUPPORT_ISSUE_TYPES = [
  { value: 'Invoice', label: 'Question about an invoice' },
  { value: 'PaymentFailed', label: 'A payment failed' },
  { value: 'Refund', label: 'Refund request' },
  { value: 'PlanChange', label: 'Changing my plan' },
  { value: 'Cancellation', label: 'Canceling my subscription' },
  { value: 'Tax', label: 'Tax on my invoice' },
  { value: 'Other', label: 'Something else' },
] as const;

export type SupportIssueType = (typeof SUPPORT_ISSUE_TYPES)[number]['value'];

export type SupportRequestStatus = 'Open' | 'InProgress' | 'Resolved';

export interface SupportRequestInput {
  issueType: SupportIssueType;
  invoiceNumber?: string;
  message: string;
}

/** A trackable billing support case. `caseNumber` looks like `BIL-000123`. */
export interface SupportRequestCase {
  id: string;
  caseNumber: string;
  issueType: string;
  invoiceNumber: string | null;
  message: string;
  status: SupportRequestStatus;
  billingContactEmail: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

/** Result of `POST /api/billing/reactivate`: a company that is already `Canceled` is sent through a new checkout,
 * otherwise the scheduled cancel/downgrade is simply undone and `checkout` is null. */
export interface ReactivateResult {
  checkout: CheckoutSessionResponse | null;
}
